import { CheckCircleTwoTone, HeartOutlined, QuestionCircleTwoTone } from '@ant-design/icons'
import { Card, Checkbox, Collapse, Layout, List, Progress, Space, Tag, Typography } from 'antd'
import { Header, Content } from 'antd/es/layout/layout'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HeaderMenu } from '../../components/header-menu'
import { friendsAndFamilyChecklistItems as checklistItems } from './adulting-101-checklists'

const { Title, Paragraph } = Typography

const faqItems = [
  {
    question: 'Why does showing up matter so much, even for small plans?',
    answer: (
      <>
        Canceling on people repeatedly signals they aren't a priority, even if that's not what you mean. Following through on plans is a
        form of <Link to="/adulting-101/responsibility">Responsibility</Link> — people trust those who keep their word.
      </>
    ),
  },
  {
    question: 'How do I actually listen instead of waiting for my turn to talk?',
    answer: (
      <>
        Focus on understanding what they're saying instead of formulating your response while they're still talking. This is the exact skill
        covered in <Link to="/adulting-101/communication-skills">Communication Skills</Link>.
      </>
    ),
  },
  {
    question: 'How do I support someone who is struggling without making it awkward?',
    answer: (
      <>
        Just show up and listen — you don't need perfect advice. Ask "do you want help or just someone to listen?" This same supportive
        instinct is discussed in <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link>.
      </>
    ),
  },
  {
    question: 'Is it okay to be honest even if it might upset someone?',
    answer: (
      <>
        Yes, as long as you're kind about it. Honest friends are more valuable than ones who just tell you what you want to hear. Balancing
        honesty with tact is part of good <Link to="/adulting-101/communication-skills">Communication Skills</Link>.
      </>
    ),
  },
  {
    question: "How do I respect someone's time and space without pulling away entirely?",
    answer: (
      <>
        Notice when someone seems busy or needs space, and don't demand constant attention. Respecting boundaries is a key part of{' '}
        <Link to="/adulting-101/emotional-intelligence">Emotional Intelligence</Link>.
      </>
    ),
  },
  {
    question: 'What does a real apology actually look like?',
    answer: (
      <>
        A real apology names what you did, shows you understand why it hurt them, and includes a genuine effort not to repeat it. "Sorry you
        feel that way" isn't an apology. This connects to owning your mistakes, just like in{' '}
        <Link to="/adulting-101/responsibility">Responsibility</Link>.
      </>
    ),
  },
  {
    question: "Why should I celebrate other people's wins instead of just my own?",
    answer: (
      <>
        Celebrating someone else's success without jealousy builds trust and shows you actually want good things for them. It's part of the
        same generosity of spirit discussed in <Link to="/adulting-101/spiritual-awareness">Spiritual Awareness</Link>.
      </>
    ),
  },
  {
    question: "What if I'm bad at staying in touch with people?",
    answer: (
      <>
        Set a goal to text or call one person a week instead of waiting for things to happen naturally. Building this into your routine is
        the same discipline covered in <Link to="/adulting-101/time-management">Time Management</Link>.
      </>
    ),
  },
]

export function Adulting101FriendsAndFamilyPage() {
  const [completedIds, setCompletedIds] = useState<Set<string>>(() => new Set())
  const completedCount = completedIds.size
  const completionPercent = Math.round((completedCount / checklistItems.length) * 100)

  return (
    <Layout>
      <Header>
        <h1>Friends and Family</h1>
      </Header>
      <HeaderMenu selected="adulting-101" />
      <Content style={{ padding: '20px', textAlign: 'left', background: 'linear-gradient(180deg, #fff1f0 0%, #ffffff 220px)' }}>
        <Title level={2}>Friendships and family relationships are among the most important things in life.</Title>
        <Paragraph type="secondary">
          Being a good friend and family member means showing up, being honest, listening, and caring — not just about yourself, but about
          the people around you. These relationships define who you are and support you through everything.
        </Paragraph>

        <Card
          title={
            <Space>
              <HeartOutlined style={{ fontSize: '20px', color: '#f5222d' }} />
              Friends and Family Checklist
            </Space>
          }
          style={{ marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          extra={<Progress type="circle" percent={completionPercent} size={56} />}
        >
          <List
            dataSource={checklistItems}
            renderItem={(item) => (
              <List.Item>
                <Checkbox
                  checked={completedIds.has(item.id)}
                  onChange={(event) => {
                    setCompletedIds((currentIds) => {
                      const nextIds = new Set(currentIds)
                      if (event.target.checked) {
                        nextIds.add(item.id)
                      } else {
                        nextIds.delete(item.id)
                      }
                      return nextIds
                    })
                  }}
                >
                  {item.text}
                </Checkbox>
              </List.Item>
            )}
          />
          {completionPercent === 100 ? (
            <Tag color="success" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} style={{ marginTop: '12px' }}>
              Congratulations, you're being a great friend and family member!
            </Tag>
          ) : (
            <Paragraph style={{ marginTop: '12px' }}>
              Healthy relationships take work. The effort you put in now builds a support system that will carry you through life.
            </Paragraph>
          )}
        </Card>

        <Card
          title={
            <Space>
              <QuestionCircleTwoTone />
              FAQ: Friends and Family
            </Space>
          }
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <Collapse
            items={faqItems.map((faq, index) => ({
              key: index,
              label: faq.question,
              children: <Paragraph>{faq.answer}</Paragraph>,
            }))}
          />
        </Card>
      </Content>
    </Layout>
  )
}
